import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useField, useFormikContext } from 'formik'
import { useNewTranslation } from 'hooks'
import { TFunction } from 'i18next'
import styled from 'particles/themes/styled'
import qs from 'query-string'
import _ from 'lodash'
import Text from 'particles/Text'
import RectangleButton from 'particles/Button/RectangleButton'
import { FormikTextField } from 'particles/TextInputs/TextField'
import { FormikCheckboxGroup } from 'particles/Checkbox/Group'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import InputField from 'components/Shared/Fields'
import { UploadType } from 'services/api/uploads'
import { DefaultBannerContainer, TemplateContentContainer } from '../style'
import { TemplateDetailSchema } from './index'
import { createGetConstantsByKey, selectCampaignByTemplateData } from 'state'
import {
    updateCampaignInfo,
    selectCampaignInfo,
    OBJECTIVE,
    selectTemplateDetail,
    CampaignTemplateStep,
} from 'state/campaignTemplates'
import OnFormikValueChange, { OnFormikValueChangeProps } from '../OnFormikValueChange'
import { updateCampaignInformationData } from 'state/appPreview/action/campaign'
import { fieldIdentifiers } from 'components/Shared/Comment'
import TemplateInputFieldContainer from 'components/Shared/Permission/TemplateInputFieldContainer'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { TemplateFilterSchema } from '../TemplateSelector'
import SaveChanges from '../SaveChanges'
import { selectCountrySetting } from 'state/constants'
import FileUploader, { FileState } from 'particles/FileUploader'

interface CampaignInformationProps {
    toNextSection: (isCompleted: boolean, isError: boolean) => void
    showError: boolean
    isReadOnly?: boolean
    validateSpecificField: OnFormikValueChangeProps['validateSpecificField']
    updateCurrentStepStatus: (isCompleted: boolean, isError: boolean) => void
    setNavigationDisable?: React.Dispatch<React.SetStateAction<boolean>>
    isNavigationDisable?: boolean
    openWarningClickToast?: () => void
}

interface ObjectiveProps {
    id: number
    label: string
}

const getRequiredRegistrationOptions = (t: TFunction) => {
    return [
        {
            value: true,
            label: t('campaign.template.campaignRegisterRequire.yes'),
        },
        {
            value: false,
            label: t('campaign.template.campaignRegisterRequire.no'),
        },
    ]
}

const getCustomBannerOptions = (t: TFunction) => {
    return [
        {
            value: false,
            label: t('campaign.template.campaignBanner.option.alreadHaveBanner'),
        },
        {
            value: true,
            label: t('campaign.template.campaignBanner.option.defaultImage'),
        },
    ]
}

const CampaignInformation: React.FC<CampaignInformationProps> = ({
    toNextSection,
    showError: baseShowError,
    validateSpecificField,
    updateCurrentStepStatus,
    setNavigationDisable,
    isNavigationDisable,
    openWarningClickToast,
    isReadOnly,
}) => {
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid } = params
    const [, titleMeta] = useField('title')
    const [, categoriesMeta] = useField('categories')
    const requiredRegistrationOptions = getRequiredRegistrationOptions(t)
    const customBannerOptions = getCustomBannerOptions(t)
    const campaign = useSelector(selectCampaignByTemplateData)
    const campaignConstants = useSelector(createGetConstantsByKey('campaign'))
    const campaignInfo = useSelector(selectCampaignInfo)
    const templateDetail = useSelector(selectTemplateDetail)
    const { values, setTouched } = useFormikContext<TemplateDetailSchema & TemplateFilterSchema>()
    const countrySetting = useSelector(selectCountrySetting(campaign?.country))
    const defaultBanner = countrySetting?.default_campaign_image_url
    // To control showError for Live campaign
    const [showError, setShowError] = useState(baseShowError)
    const categoryOptions = campaignConstants.campaign_categories.map((category: ObjectiveProps) => ({
        value: category.id,
        label: category.label,
    }))
    const showTitleOverLimitError =
        titleMeta.error === t('errorMessage.validation.enterCampaignNameCharacterLimit') || showError
    const showCategoriesOverLimitError =
        categoriesMeta.error === t('errorMessage.validation.selectCategoryLimit') || showError

    const fields: (keyof TemplateDetailSchema)[] = [
        'title',
        'categories',
        'images',
        'registration_enable',
        'use_default_image',
    ]
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    const showRegistrationOptions =
        templateDetail?.objective !== OBJECTIVE.appPromotion && templateDetail?.key !== 'follow'

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive || !templateDataUuid) {
            return false
        }

        return isReadOnly
    }

    const onNextClick = () => {
        if (isNavigationDisable) {
            openWarningClickToast?.()
            return
        }
        validateSpecificField(fields).then(errors => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                toNextSection(true, false)
            } else {
                if (showTitleOverLimitError || showCategoriesOverLimitError) {
                    toNextSection(false, true)
                } else {
                    toNextSection(false, false)
                }
            }
        })
    }

    const handleDebouncedChange: OnFormikValueChangeProps['callback'] = useCallback(
        fieldsValues => {
            const validFieldsValues = _.omitBy(fieldsValues, _.isNull)
            if (
                !_.isEmpty(validFieldsValues) &&
                campaignInfo &&
                (!_.isMatch(campaignInfo, fieldsValues) ||
                    !_.isEqual(campaignInfo.images, fieldsValues.images) ||
                    !_.isEqual(campaignInfo.categories, fieldsValues.categories))
            ) {
                dispatch(updateCampaignInformationData(validFieldsValues))
                if (!isCampaignLive && !isReadOnly) {
                    // update API call for live campaign should be by button
                    const validTemplateDataUuid = typeof templateDataUuid === 'string' ? templateDataUuid : ''
                    dispatch(
                        updateCampaignInfo({
                            templateDataUuid: validTemplateDataUuid,
                            ...validFieldsValues,
                            setNavigationDisable: setNavigationDisable,
                        }),
                    )
                }
            }
        },
        [campaignInfo, isCampaignLive],
    )

    const onSaveLiveCampaign = (onSuccessCallback: VoidFunction) => {
        validateSpecificField(fields).then(errors => {
            const noError = Object.keys(errors).length === 0
            const touchedObject = fields.reduce((acc, curr) => {
                return {
                    ...acc,
                    [curr]: true,
                }
            }, {})
            setTouched(touchedObject)
            if (noError) {
                if (templateDataUuid) {
                    const fieldsValues = _.pick(values, fields)
                    const validFieldsValues = _.omitBy(fieldsValues, _.isNull)

                    const validTemplateDataUuid = typeof templateDataUuid === 'string' ? templateDataUuid : ''
                    dispatch(
                        updateCampaignInfo({
                            templateDataUuid: validTemplateDataUuid,
                            ...validFieldsValues,
                            setNavigationDisable: setNavigationDisable,
                            showSuccessToast: true,
                        }),
                    )
                    onSuccessCallback()
                }
            } else {
                setShowError(true)
            }
        })
    }

    return (
        <>
            <CampaignInformationContainer>
                <OnFormikValueChange
                    callback={handleDebouncedChange}
                    fields={fields}
                    skipCallbackError={{
                        title: [t('errorMessage.validation.enterCampaignNameCharacterLimit')],
                        categories: [t('errorMessage.validation.selectCategoryLimit')],
                    }}
                    validateSpecificField={validateSpecificField}
                    showError={showError}
                    updateCurrentStepStatus={updateCurrentStepStatus}
                    disableAutoSave={disableAutoSave()}
                    // Only disableNavigation for input if campaign is not live
                    {...(!isCampaignLive && { setDisableNavigation: setNavigationDisable })}
                />
                <div className="form-title">
                    <Text scale="H4" color="primary">
                        {t('campaign.template.step2.campaignInfo.title')}
                    </Text>
                </div>
                <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                    <FormikTextField
                        name="title"
                        showInputLength
                        maxInputLength={100}
                        label={t('campaign.template.campaignName.title')}
                        subtext={t('campaign.template.campaignName.subText')}
                        isRequired
                        showError={showTitleOverLimitError}
                        validateByTouch={false}
                        labelSize="l"
                        dataTestId="test-campaign-title"
                        identifier={`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateCampaignName}`}
                    />
                </TemplateInputFieldContainer>

                <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                    <FormikCheckboxGroup
                        name="categories"
                        title={t('campaign.template.campaignCategory.title')}
                        subtitle={t('campaignEditor.setUpCampaign.enterCampaignCategorySubtext')}
                        options={categoryOptions}
                        isRequired
                        orientation="horizontal"
                        showError={showCategoriesOverLimitError}
                        validateByTouch={false}
                        labelSize="l"
                        identifier={`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateCategories}`}
                    />
                </TemplateInputFieldContainer>

                <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                    <FormikRadioButtonGroup
                        name="use_default_image"
                        title={t('campaign.template.campaignBanner.title')}
                        subtitle={t('campaign.template.campaignBanner.defaultImage.subtext')}
                        options={customBannerOptions}
                        isRequired
                        showError={showError}
                        validateByTouch={false}
                        labelSize="l"
                        dataTestId="test-default-banner-toggle"
                        identifier={`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateBannerImage}`}
                    />
                </TemplateInputFieldContainer>

                {!values.use_default_image ? (
                    <TemplateInputFieldContainer checkForLiveEditBriefAndDeadline>
                        <InputField.ImageUploadField
                            presetType={UploadType.CAMPAIGN}
                            maxFiles={10}
                            name={'images'}
                            uploadContainerWidth="100%"
                            maxWidth="770px"
                            placeholder={t('placeholderText.dragDropFilesDimension')}
                            isRequired={true}
                            showError={showError}
                            validateByTouch={false}
                            requiredHeight={750}
                            requiredWidth={1000}
                            labelSize="l"
                            dataTestId="test-banner-upload"
                        />
                    </TemplateInputFieldContainer>
                ) : (
                    <DefaultBannerContainer>
                        <FileUploader
                            label={t('campaign.template.defaultBanner.title')}
                            isRequired
                            subtext={t('campaign.template.defaultBanner.subtext')}
                            uploadedFiles={
                                [
                                    {
                                        preview: defaultBanner,
                                        resource_type: 'image',
                                    },
                                ] as FileState[]
                            }
                            width={80}
                        />
                    </DefaultBannerContainer>
                )}

                {showRegistrationOptions && (
                    <TemplateInputFieldContainer>
                        <FormikRadioButtonGroup
                            name="registration_enable"
                            title={t('campaign.template.campaignRegisterRequire.title')}
                            subtitle={t('campaign.template.campaignRegisterRequire.subText')}
                            options={requiredRegistrationOptions}
                            isRequired
                            showError={showError}
                            validateByTouch={false}
                            labelSize="l"
                            dataTestId="test-registration"
                            identifier={`${CampaignTemplateStep.CAMPAIGN_INFO}_${fieldIdentifiers.templateRegistrationEnable}`}
                        />
                    </TemplateInputFieldContainer>
                )}

                <EditTemplateRestricted>
                    <div className="button">
                        <RectangleButton type={'button'} size={'l'} label={t('button.next')} onClick={onNextClick} />
                    </div>
                </EditTemplateRestricted>
            </CampaignInformationContainer>
            <SaveChanges onSave={onSaveLiveCampaign} checkForLiveEditBriefAndDeadline />
        </>
    )
}

export const CampaignInformationContainer = styled(TemplateContentContainer)``
export default CampaignInformation
